<template>
  <div class="inflow_channel_analytics clearfix" :class='{"amp" : applyData.bAMP}'>
    <app-chart chartType="bar" v-if="barChartOptions && !applyData.bAMP"
      class="bar_chart"
      :width="140"
      :height="170"
      :chartOptions="barChartOptions">
    </app-chart>
    <app-chart chartType="bar" v-if="barChartOptions && applyData.bAMP"
       class="bar_chart"
       :width="140"
       :height="280"
       :chartOptions="barChartOptions">
    </app-chart>
    <template v-if="hasExternalData">
      <app-chart chartType="pie" v-if="pieChartOptions && !applyData.bAMP"
        class="pie_chart"
        :width="300"
        :height="240"
        :chartOptions="pieChartOptions">
      </app-chart>
      <app-chart chartType="pie" v-if="pieChartOptions && applyData.bAMP"
         class="pie_chart"
         :width="300"
         :height="282"
         :chartOptions="pieChartOptions">
      </app-chart>
      <svg v-if="guidePathData" class="guide_line"
        :width="guidePathData.width"
        :height="guidePathData.height"
        :style="{ left: guidePathData.leftOffset + 'px' }">
        <line x1="0" y1="100%" x2="100%" y2="100%"/>
        <line
          x1="0" :y1="guidePathData.startPoint.y"
          :x2="guidePathData.endPoint.x" :y2="guidePathData.endPoint.y"/>
      </svg>
    </template>
    <div v-else class="no-external">
      <p>{{ $t('analytics.noExternalFlow') }}</p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getPercentage, thousand, browserInfo } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import AppChart from '@/components/chart'

export default {
  name: 'InflowChannelAnalytics',
  components: {
    AppChart
  },
  props: [
    'applyData',
    'inflowData'
  ],
  data () {
    return {
      containerWidth: 0
    }
  },
  computed: {
    ...mapState(['resizeTimestamp']),
    guidePathData () {
      const containerWidth = this.containerWidth - (this.applyData.bAMP ? 80 : 0)

      if (containerWidth <= 0) {
        return null
      }

      function getDistance (a, b) {
        const x = b.x - a.x
        const y = b.y - a.y
        return Math.sqrt(x * x + y * y)
      }

      const r = this.applyData.bAMP ? 97 : 66
      const leftOffset = 130
      const rightOffset = 150
      const height = this.applyData.bAMP ? 196 : 133

      const width = containerWidth - leftOffset - rightOffset
      const total = this.countData.external + this.countData.internal
      const ratio = this.countData.external / total

      const startPoint = {
        x: 0,
        y: height - (height * ratio)
      }

      const centerPoint = {
        x: width,
        y: r
      }

      const centerLineLength = getDistance(startPoint, centerPoint)
      const angleBelow = Math.acos(width / centerLineLength)
      const angleAbove = Math.asin(r / centerLineLength)

      let angle

      if (ratio >= 0.5) {
        angle = angleAbove - angleBelow
      } else {
        angle = angleAbove + angleBelow
      }

      const endPoint = {
        x: width - (r * Math.sin(angle)),
        y: r - (r * Math.cos(angle))
      }

      const data = {
        leftOffset,
        width,
        height,
        startPoint,
        endPoint
      }
      return data
    },
    countData () {
      const data = this.inflowData
      const domain = this.applyData.domain
      const countData = {
        internal: 0,
        external: 0,
        direct: 0,
        search: 0,
        social: 0,
        referral: 0
      }

      const searchTerms = ['search', 'google', 'bing', 'baidu', 'so.com']
      const socialTerms = ['youtube', 'facebook', 'twitter', 'instagram', 'linkedin', 'tumblr', 'blog', 'cafe']

      function hasTextInArray (text, arr) {
        for (const item of arr) {
          if (text.indexOf(item) !== -1) {
            return true
          }
        }
        return false
      }

      for (const item of data.items) {
        const referer = item.refer
        const count = item.count

        if (referer.indexOf(domain) !== -1) {
          countData.internal += count
        } else {
          countData.external += count

          if (referer === 'Direct') {
            countData.direct += count
          } else if (hasTextInArray(referer, searchTerms)) {
            countData.search += count
          } else if (hasTextInArray(referer, socialTerms)) {
            countData.social += count
          } else {
            countData.referral += count
          }
        }
      }
      return countData
    },
    hasInternalData () {
      return !!this.countData.internal
    },
    hasExternalData () {
      return !!this.countData.external
    },
    barChartOptions () {
      const countData = this.countData
      const internalCount = countData.internal
      let externalCount = this.countData.external

      if (this.inflowData.referEtcCount) {
        externalCount += this.inflowData.referEtcCount
      }
      const total = internalCount + externalCount
      const internalPercentage = getPercentage(internalCount, total, 1)
      const externalPercentage = getPercentage(externalCount, total, 1)
      const internalLabel = this.$t('analytics.content.inflow.internal')
      const externalLabel = this.$t('analytics.content.inflow.external')
      const allBorderRound = [10, 10, 10, 10]

      return {
        grid: {
          top: !this.applyData.bAMP ? 20 : 50,
          right: 0,
          left: 40,
          height: !this.applyData.bAMP ? 132 : 198,
          containLabel: false
        },
        legend: {
          show: false
        },
        tooltip: {
          formatter (params) {
            const content = []

            for (const param of params) {
              content.push(
                [`${param.marker}${param.seriesName} :`, `${param.value}%`]
              )
            }
            return getTooltipHtml(content, params[0].axisValue)
          }
        },
        xAxis: {
          type: 'category',
          data: [this.$t('analytics.content.inflow.title2')],
          axisLabel: {
            show: false
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#d8d8d8'
            }
          }
        },
        yAxis: {
          axisLabel: {
            formatter: '{value}%'
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#d8d8d8'
            }
          },
          splitLine: {
            show: false
          },
          axisTick: {
            show: true
          }
        },
        series: [
          {
            name: externalLabel,
            type: 'bar',
            barWidth: 20,
            stack: 'stack-0',
            data: [externalPercentage],
            itemStyle: {
              barBorderRadius: !this.hasInternalData ? allBorderRound : [0, 0, 10, 10]
            },
            label: {
              show: this.hasExternalData,
              position: 'bottom',
              formatter: externalLabel,
              color: '#1199af',
              fontSize: 12,
              fontWeight: 500,
              // fix BEUS-2311
              distance: browserInfo.name === 'ie' ? 5 : 8
            }
          },
          {
            name: internalLabel,
            type: 'bar',
            barWidth: 20,
            stack: 'stack-0',
            data: [internalPercentage],
            itemStyle: {
              barBorderRadius: !this.hasExternalData ? allBorderRound : [10, 10, 0, 0]
            },
            label: {
              show: this.hasInternalData,
              position: 'top',
              formatter: internalLabel,
              color: '#9fce12',
              fontSize: 12,
              fontWeight: 500,
              distance: 8
            }
          }
        ],
        color: ['#1199af', '#9fce12']
      }
    },
    pieChartOptions () {
      const countData = this.countData
      const data = ['direct', 'search', 'social', 'referral'].map(item => {
        return {
          name: this.$options.filters.upperFirst(item),
          value: countData[item]
        }
      }).filter(item => item.value > 0)

      return {
        tooltip: {
          formatter (param) {
            const content = [
              [
                `${param.data.name} : ${param.percent.toFixed(1)}%`,
                thousand(param.data.value)
              ]
            ]
            return getTooltipHtml(content, param.seriesName)
          }
        },
        series: [
          {
            type: 'pie',
            name: this.$t('analytics.content.inflow.visitingChannel'),
            data,
            label: {
              show: true,
              color: '#444',
              fontSize: 12
            },
            labelLine: {
              lineStyle: {
                color: '#c1c1c1'
              }
            },
            radius: this.applyData.bAMP ? [40, 97] : [25, 66]
          }
        ],
        color: ['#1199af', '#2dbfd7', '#a9e7f1', '#ebebeb']
      }
    }
  },
  watch: {
    resizeTimestamp (value) {
      this.containerWidth = this.$el.offsetWidth
    }
  },
  mounted () {
    this.containerWidth = this.$el.offsetWidth
  }
}
</script>

<style lang="scss">
.inflow_channel_analytics {
  &.amp {
    position: absolute;
    top : 160px;
    left: 50px;
    width: 100%;
    display: inline-block;
    vertical-align: middle;
    max-width: 700px;

    .guide_line {
      bottom : 39px;
    }

    .pie_chart {
      right : 40px;
      bottom : -2px;
    }

    .bar_chart {
      margin-bottom: 8px;
    }
  }

  position: relative;

  .no-external {
    position: absolute;
    left: 170px;
    top: 0;
    right: 80px;
    height: 170px;

    &:after {
      content: '';
      display: inline-block;
      width: 100%;
      border-bottom: 1px solid #d8d8d8;
      position: absolute;
      left: 0;
      bottom: 17px;
    }

    &> p {
      @include positionXYcenter;
      font-size: 13px;
      color: #767676;
    }
  }

  .bar_chart {
    float: left;
    margin-left: 30px;
  }
  .pie_chart {
    position: absolute;
    right: 0;
    bottom: -18px
  }
  .guide_line {
    pointer-events: none;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 17px;

    line {
      stroke: #aaa;
      stroke-width: 1;
      stroke-dasharray: 2 2;
    }
  }
}
</style>
